/**
 * Impact Calibration Utility
 * Calculates loss scenarios aligned with cyber insurance coverage categories,
 * Annualized Loss Expectancy (ALE), and applies insurance offsets for
 * realistic financial impact modeling.
 *
 * Scenarios dynamically adjust based on:
 * - Threat profile (industry vertical)
 * - Business scoping responses (size, data types, dependencies)
 * - Organizational characteristics (publicly traded, gov contractor, etc.)
 * - Framework selections (HIPAA, PCI, etc.)
 */

import { deriveMarketSegment, getSegmentModifiers } from './marketSegment'
import { calculateCategoryBreachCosts } from './sensitiveDataCategories'
import claimsBenchmarks from '../data/claimsBenchmarks.json'

/**
 * Loss scenario categories aligned with cyber insurance policy coverage
 */
const LOSS_SCENARIOS = {
  financial_theft: {
    id: 'financial_theft',
    name: 'Financial Theft & Fraud',
    shortName: 'Financial Theft',
    description: 'Wire fraud, business email compromise, account takeover, and payment fraud resulting in direct financial loss.',
    detailedDescription: 'Includes social engineering attacks leading to unauthorized wire transfers, compromised payment systems, fraudulent invoicing, and account credential theft enabling unauthorized transactions.',
    icon: 'currency-dollar',
    category: 'first-party',
    coverageType: 'Crime / Social Engineering',
    contextFactors: ['payment_processing', 'wire_transfers', 'accounts_payable', 'financial_controls'],
    baseComponents: {
      averageLoss: 125000,           // Average direct theft amount
      recoveryEfforts: 15000,        // Attempts to recover funds
      investigationCost: 20000,      // Forensic investigation
      legalCost: 25000,              // Legal fees for recovery
      processImprovements: 15000,    // Control enhancements post-incident
      employeeTraining: 10000        // Enhanced security awareness
    },
    probabilityByMaturity: {
      initial: 0.28,
      developing: 0.20,
      defined: 0.12,
      managed: 0.05,
      optimizing: 0.02
    }
  },

  cyber_extortion: {
    id: 'cyber_extortion',
    name: 'Cyber Extortion',
    shortName: 'Extortion',
    description: 'Ransomware attacks, DDoS extortion, and data theft with ransom demands threatening to publish stolen data.',
    detailedDescription: 'Encompasses ransomware encrypting critical systems, extortion threats to release sensitive data, DDoS attacks with payment demands, and double/triple extortion tactics combining multiple threats.',
    icon: 'lock-closed',
    category: 'first-party',
    coverageType: 'Cyber Extortion',
    contextFactors: ['backup_systems', 'endpoint_protection', 'network_segmentation', 'incident_response'],
    baseComponents: {
      ransomPayment: 250000,         // Median ransom demand (may or may not pay)
      downtimeCost: 0,               // Calculated dynamically
      recoveryLabor: 100000,         // IT recovery effort
      dataRecoveryTools: 25000,      // Decryption/recovery tools
      negotiatorFees: 15000,         // Ransomware negotiation services
      thirdPartyIR: 75000,           // External incident response
      reputationCost: 30000          // Brand damage
    },
    averageDowntimeHours: 336,       // Average 14 days
    probabilityByMaturity: {
      initial: 0.38,
      developing: 0.26,
      defined: 0.16,
      managed: 0.08,
      optimizing: 0.03
    }
  },

  data_breach: {
    id: 'data_breach',
    name: 'Data & Privacy Breach',
    shortName: 'Data Breach',
    description: 'Unauthorized access to sensitive data including PII, PHI, financial records, and intellectual property.',
    detailedDescription: 'Covers external intrusions, compromised credentials, SQL injection, API vulnerabilities, and any unauthorized access resulting in data exposure. Includes privacy violations under CCPA, GDPR, HIPAA, and state laws.',
    icon: 'shield-exclamation',
    category: 'first-party',
    coverageType: 'Data Breach Response',
    contextFactors: ['data_sensitivity', 'record_count', 'data_classification', 'encryption_status'],
    baseComponents: {
      perRecordCost: 165,            // IBM/Ponemon average
      forensicsBase: 75000,          // Digital forensics
      notificationCost: 25000,       // Notification services base
      creditMonitoring: 0,           // Calculated per record
      legalCounsel: 100000,          // Breach counsel
      regulatoryFine: 0,             // Calculated based on context
      prCrisis: 50000,               // Crisis communications
      customerChurn: 75000           // Lost business
    },
    probabilityByMaturity: {
      initial: 0.32,
      developing: 0.22,
      defined: 0.14,
      managed: 0.06,
      optimizing: 0.02
    }
  },

  business_interruption: {
    id: 'business_interruption',
    name: 'Business Interruption',
    shortName: 'Bus. Interruption',
    description: 'Extended unavailability of critical systems, cloud services, or infrastructure causing operational disruption.',
    detailedDescription: 'Includes internal system failures, cloud/SaaS provider outages, infrastructure attacks, and any event preventing normal business operations. Covers lost revenue, extra expenses, and dependent business interruption.',
    icon: 'server',
    category: 'first-party',
    coverageType: 'Business Interruption',
    contextFactors: ['cloud_dependency', 'redundancy', 'recovery_time_objective', 'revenue_per_hour'],
    baseComponents: {
      downtimeCost: 0,               // Calculated dynamically
      emergencyIT: 35000,            // Emergency support
      cloudFailover: 20000,          // Failover costs
      customerCredits: 15000,        // SLA penalties
      overtimeLabor: 25000,          // Staff overtime
      expeditedShipping: 10000,      // Rush replacement hardware
      lostOpportunity: 50000,        // Missed business
      contingentBI: 25000            // Dependent business impact
    },
    averageDowntimeHours: 72,        // Average 3 days
    probabilityByMaturity: {
      initial: 0.42,
      developing: 0.28,
      defined: 0.16,
      managed: 0.07,
      optimizing: 0.02
    }
  },

  third_party: {
    id: 'third_party',
    name: 'Third-Party Liability',
    shortName: '3rd Party',
    description: 'Liability arising from vendor breaches, supply chain compromises, and downstream customer impacts.',
    detailedDescription: 'Covers vendor/supplier security incidents affecting your data, software supply chain attacks (e.g., compromised updates), MSP breaches, and your liability when your incident affects customers or partners.',
    icon: 'link',
    category: 'third-party',
    coverageType: 'Network Security Liability',
    contextFactors: ['vendor_count', 'vendor_access_level', 'supply_chain_complexity', 'customer_contracts'],
    baseComponents: {
      vendorBreachShare: 150000,     // Your share of vendor incident
      supplyChainRemediation: 75000, // Removing compromised software
      customerNotification: 40000,   // Notifying affected customers
      contractualPenalties: 50000,   // SLA/contract breaches
      legalDefense: 100000,          // Defense costs
      settlementReserve: 200000,     // Potential settlements
      auditCosts: 25000              // Third-party security audits
    },
    probabilityByMaturity: {
      initial: 0.25,
      developing: 0.18,
      defined: 0.12,
      managed: 0.06,
      optimizing: 0.03
    }
  },

  regulatory: {
    id: 'regulatory',
    name: 'Regulatory & Compliance',
    shortName: 'Regulatory',
    description: 'Regulatory investigations, compliance fines, and enforcement actions from data protection authorities.',
    detailedDescription: 'Includes HIPAA OCR investigations, state AG actions, GDPR/CCPA fines, PCI DSS non-compliance penalties, SEC enforcement, and costs to remediate compliance gaps discovered during audits.',
    icon: 'scale',
    category: 'first-party',
    coverageType: 'Regulatory Defense',
    contextFactors: ['regulatory_framework', 'data_jurisdiction', 'previous_violations', 'audit_history'],
    baseComponents: {
      regulatoryFine: 150000,        // Base fine amount
      investigationResponse: 50000,  // Responding to inquiries
      legalDefense: 125000,          // Regulatory defense counsel
      remediationCosts: 75000,       // Required improvements
      auditPreparation: 25000,       // Audit support
      monitoringRequirements: 30000, // Ongoing compliance monitoring
      consentDecreeCosts: 50000      // If consent order issued
    },
    probabilityByMaturity: {
      initial: 0.22,
      developing: 0.15,
      defined: 0.08,
      managed: 0.04,
      optimizing: 0.01
    }
  },

  insider_threat: {
    id: 'insider_threat',
    name: 'Insider Threat',
    shortName: 'Insider',
    description: 'Malicious or negligent actions by employees, contractors, or privileged users causing data loss or system damage.',
    detailedDescription: 'Encompasses intentional data theft by departing employees, sabotage, privilege abuse, accidental data exposure, and negligent security practices. Often harder to detect and may involve IP theft.',
    icon: 'user-minus',
    category: 'first-party',
    coverageType: 'Crime / Employee Dishonesty',
    contextFactors: ['employee_count', 'privileged_users', 'offboarding_process', 'dlp_controls'],
    baseComponents: {
      dataTheftValue: 100000,        // Value of stolen data/IP
      investigationCost: 40000,      // Internal investigation
      forensicsAnalysis: 35000,      // Digital forensics
      legalAction: 60000,            // Civil/criminal proceedings
      terminationCosts: 15000,       // HR/legal for termination
      controlEnhancements: 30000,    // DLP, access controls
      backgroundChecks: 10000,       // Enhanced screening
      productivityLoss: 25000        // Team disruption
    },
    probabilityByMaturity: {
      initial: 0.20,
      developing: 0.15,
      defined: 0.10,
      managed: 0.05,
      optimizing: 0.02
    }
  }
}

/**
 * Industry-specific multipliers for each scenario type
 * Based on threat profile vertical
 */
const INDUSTRY_MULTIPLIERS = {
  'TP-HC': {
    financial_theft: 0.9,
    cyber_extortion: 1.6,      // Healthcare heavily targeted
    data_breach: 1.5,          // PHI is high-value
    business_interruption: 1.4, // Patient care disruption
    third_party: 1.1,
    regulatory: 1.8,           // HIPAA enforcement
    insider_threat: 1.2
  },
  'TP-FIN-BK': {
    financial_theft: 1.6,      // Primary target for theft
    cyber_extortion: 1.3,
    data_breach: 1.4,
    business_interruption: 1.5, // Financial transactions critical
    third_party: 1.3,
    regulatory: 1.5,           // OCC/FDIC/state regulators
    insider_threat: 1.4        // High-value insider access
  },
  'TP-FIN-INS': {
    financial_theft: 1.4,
    cyber_extortion: 1.2,
    data_breach: 1.3,
    business_interruption: 1.3,
    third_party: 1.2,
    regulatory: 1.4,           // State insurance commissioners
    insider_threat: 1.3
  },
  'TP-NP': {
    financial_theft: 1.3,      // Wire fraud to nonprofits increasing
    cyber_extortion: 1.2,
    data_breach: 1.0,
    business_interruption: 0.8, // Lower revenue impact
    third_party: 0.9,
    regulatory: 1.1,
    insider_threat: 1.1
  },
  'TP-DEFAULT': {
    financial_theft: 1.0,
    cyber_extortion: 1.0,
    data_breach: 1.0,
    business_interruption: 1.0,
    third_party: 1.0,
    regulatory: 1.0,
    insider_threat: 1.0
  }
}

/**
 * Context factor adjustments based on business scoping responses
 * Maps question IDs to scenario adjustments
 */
const CONTEXT_FACTOR_ADJUSTMENTS = {
  // Payment processing increases financial theft risk
  'bs-8': {
    'credit-cards': { financial_theft: 1.3, regulatory: 1.2 },
    'ach-wire': { financial_theft: 1.4 },
    'cryptocurrency': { financial_theft: 1.2, regulatory: 1.1 }
  },
  // Third-party dependencies increase supply chain risk
  'bs-10': {
    'extensive': { third_party: 1.5, business_interruption: 1.3 },
    'moderate': { third_party: 1.2, business_interruption: 1.1 },
    'minimal': { third_party: 0.8 }
  },
  // Data sensitivity affects breach and regulatory costs
  'bs-4': {
    'phi': { data_breach: 1.4, regulatory: 1.5 },
    'pii': { data_breach: 1.2, regulatory: 1.2 },
    'financial': { data_breach: 1.3, regulatory: 1.3, financial_theft: 1.2 },
    'ip': { insider_threat: 1.4, data_breach: 1.1 }
  },
  // Employee count affects insider threat
  'bs-2': {
    '>1000': { insider_threat: 1.4, business_interruption: 1.2 },
    '251-1000': { insider_threat: 1.2 },
    '51-250': { insider_threat: 1.0 },
    '<50': { insider_threat: 0.8 }
  },
  // Cloud dependency affects business interruption
  'bs-9': {
    'cloud-first': { business_interruption: 1.3, third_party: 1.2 },
    'hybrid': { business_interruption: 1.1 },
    'on-premises': { business_interruption: 0.9, cyber_extortion: 1.1 }
  }
}

/**
 * Organizational characteristic adjustments
 * Applied based on org characteristics selected in business scoping
 */
const ORG_CHARACTERISTIC_ADJUSTMENTS = {
  'publicly-traded': {
    regulatory: 1.4,           // SEC disclosure requirements
    data_breach: 1.2,          // Higher scrutiny
    third_party: 1.2
  },
  'government-contractor': {
    regulatory: 1.5,           // CMMC/FedRAMP requirements
    data_breach: 1.3,
    cyber_extortion: 1.2
  },
  'processes-payments': {
    financial_theft: 1.4,
    regulatory: 1.3,           // PCI DSS
    data_breach: 1.2
  },
  'critical-infrastructure': {
    cyber_extortion: 1.5,      // High-value target
    business_interruption: 1.4,
    regulatory: 1.3
  }
}

/**
 * Framework-based adjustments
 * Applied when specific compliance frameworks are selected
 */
const FRAMEWORK_ADJUSTMENTS = {
  'hipaa': {
    data_breach: 1.3,
    regulatory: 1.5,
    insider_threat: 1.2
  },
  'pci-dss': {
    financial_theft: 1.2,
    data_breach: 1.2,
    regulatory: 1.4
  },
  'gdpr': {
    data_breach: 1.4,
    regulatory: 1.6,
    third_party: 1.2
  },
  'ccpa': {
    data_breach: 1.2,
    regulatory: 1.3
  },
  'cmmc': {
    data_breach: 1.3,
    regulatory: 1.4,
    insider_threat: 1.3
  },
  'sox': {
    regulatory: 1.4,
    insider_threat: 1.3,
    financial_theft: 1.2
  }
}

// ── Claims Benchmark Helpers ──────────────────────────────────────────────────

/**
 * Resolve threat profile vertical key to claims benchmark sector data.
 * Returns the sector profile from claimsBenchmarks.json or null.
 */
function getClaimsBenchmarkForSector(verticalKey) {
  if (!claimsBenchmarks.sectorProfiles) return null
  const match = Object.entries(claimsBenchmarks.sectorProfiles)
    .filter(([k]) => k !== '_doc')
    .find(([, sector]) => sector.mapsToThreatProfile === verticalKey)
  return match ? { key: match[0], ...match[1] } : null
}

/**
 * Resolve annual revenue to the matching claims benchmark revenue band.
 */
function getClaimsBenchmarkForRevenueBand(annualRevenue) {
  const rb = claimsBenchmarks.revenueBands
  if (!rb) return null
  if (!annualRevenue || annualRevenue <= 0) return rb.nano || null
  if (annualRevenue >= 2000000000) return rb.mid || null
  if (annualRevenue >= 300000000) return rb.small || null
  if (annualRevenue >= 50000000) return rb.micro || null
  return rb.nano || null
}

/**
 * Enrich LOSS_SCENARIOS base components with claims benchmark data.
 * Claims data replaces hardcoded values where a sector-specific benchmark exists.
 * Falls back to hardcoded defaults when claims data is unavailable.
 */
function getClaimsEnrichedBaseComponents(scenarioId, verticalKey, annualRevenue) {
  const scenario = LOSS_SCENARIOS[scenarioId]
  if (!scenario) return { enrichedComponents: { ...({}) }, claimsSource: null, enrichmentApplied: false }

  const sectorData = getClaimsBenchmarkForSector(verticalKey)
  const revenueBandData = annualRevenue ? getClaimsBenchmarkForRevenueBand(annualRevenue) : null
  const components = { ...scenario.baseComponents }
  let enrichmentApplied = false

  // Only enrich when the sector maps to a specific threat profile (not the catch-all TP-DEFAULT)
  const hasSectorData = sectorData && sectorData.mapsToThreatProfile === verticalKey && verticalKey !== 'TP-DEFAULT'

  switch (scenarioId) {
    case 'data_breach':
      if (hasSectorData && sectorData.crisisServices) {
        const css = sectorData.crisisServices
        if (css.forensics && css.forensics > 0) {
          components.forensicsBase = css.forensics
          enrichmentApplied = true
        }
        if (css.notification && css.notification > 0) {
          components.notificationCost = css.notification
          enrichmentApplied = true
        }
        if (css.legalGuidance && css.legalGuidance > 0) {
          components.legalCounsel = css.legalGuidance
          enrichmentApplied = true
        }
      }
      break

    case 'cyber_extortion': {
      if (hasSectorData && sectorData.crisisServices?.forensics) {
        components.thirdPartyIR = sectorData.crisisServices.forensics
        enrichmentApplied = true
      }
      break
    }

    case 'financial_theft': {
      const becData = claimsBenchmarks.causeOfLoss?.bec
      if (becData?.averageIncidentCost) {
        components.averageLoss = becData.averageIncidentCost
        enrichmentApplied = true
      }
      break
    }

    case 'business_interruption': {
      // Use sector-level crisis services as a scaling reference
      if (hasSectorData && sectorData.crisisServices?.total) {
        const smeAvg = claimsBenchmarks.smeAggregate?.averageCrisisServicesCost || 152000
        if (sectorData.crisisServices.total > smeAvg) {
          const ratio = sectorData.crisisServices.total / smeAvg
          components.emergencyIT = Math.round(components.emergencyIT * Math.min(ratio, 2.5))
          components.overtimeLabor = Math.round(components.overtimeLabor * Math.min(ratio, 2.5))
          enrichmentApplied = true
        }
      }
      break
    }

    case 'third_party': {
      const tpData = claimsBenchmarks.causeOfLoss?.thirdPartyMalicious
      if (tpData?.averageIncidentCost) {
        const currentTotal = Object.values(components).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        if (tpData.averageIncidentCost > currentTotal * 0.5) {
          const cappedScale = Math.min(tpData.averageIncidentCost / currentTotal, 2.0)
          if (components.vendorBreachShare) components.vendorBreachShare = Math.round(components.vendorBreachShare * cappedScale)
          if (components.legalDefense) components.legalDefense = Math.round(components.legalDefense * cappedScale)
          if (components.settlementReserve) components.settlementReserve = Math.round(components.settlementReserve * cappedScale)
          enrichmentApplied = true
        }
      }
      break
    }
  }

  return {
    enrichedComponents: components,
    claimsSource: enrichmentApplied ? (sectorData?.source || 'netdiligence-2025') : null,
    enrichmentApplied,
    sectorUsed: hasSectorData ? sectorData.key : null,
    revenueBandUsed: revenueBandData?.label || null
  }
}

/**
 * Get segment-adjusted loss parameters based on business scoping responses.
 */
export function getSegmentAdjustedParams(businessScopingResponses, estimate = 'likely') {
  const segmentData = deriveMarketSegment(businessScopingResponses)
  const modifiers = segmentData.modifiers

  return {
    segment: segmentData.segment,
    segmentLabel: segmentData.segmentLabel,
    costPerRecord: modifiers.costPerRecord[estimate],
    downtimeDays: modifiers.downtimeDays[estimate],
    recoveryCost: modifiers.recoveryCost[estimate],
    directLossMultiplier: modifiers.directLossMultiplier[estimate],
    threatActorSophistication: modifiers.threatActorSophistication,
    regulatoryScrutinyLevel: modifiers.regulatoryScrutinyLevel,
    initiativeCostMultiplier: modifiers.initiativeCostMultiplier
  }
}

/**
 * Maturity response score mapping.
 * Maturity responses are stored as { value: 'yes'|'mostly'|'partially'|'no', weight }
 * This converts to a 0-3 numeric score.
 */
const MATURITY_SCORE_MAP = { yes: 3.0, mostly: 2.5, partially: 1.5, no: 0 }

function getMaturityScore(maturityResponses, questionId) {
  const response = maturityResponses?.[questionId]
  if (!response) return null
  const value = typeof response === 'object' ? response.value : response
  return MATURITY_SCORE_MAP[value] ?? null
}

/**
 * Calculate maturity-informed adjustments to loss scenario parameters.
 *
 * Uses individual maturity question responses to adjust:
 * - Recovery time (backup testing → ransomware downtime)
 * - Detection speed (24/7 monitoring → breach scope/cost)
 * - Attack probability (MFA, patching, segmentation → likelihood)
 * - Propagation scope (network segmentation → blast radius)
 *
 * Returns adjustment factors that are applied within calculateScenarioLoss.
 *
 * Maturity questions consumed:
 *   ma-0  — MFA / strong identity controls
 *   ma-22 — EDR / advanced monitoring deployed
 *   ma-23 — Backups regularly tested for recovery
 *   ma-24 — Systems patched based on severity and risk
 *   ma-26 — Centralized logging and monitoring
 *   ma-56 — Network segmentation between critical/user/untrusted zones
 *   ma-59 — 24/7 security monitoring (SOC/MDR)
 */
function getMaturityControlAdjustments(maturityResponses) {
  if (!maturityResponses || typeof maturityResponses !== 'object') {
    return null
  }

  const mfa = getMaturityScore(maturityResponses, 'ma-0')
  const edr = getMaturityScore(maturityResponses, 'ma-22')
  const backupTesting = getMaturityScore(maturityResponses, 'ma-23')
  const patching = getMaturityScore(maturityResponses, 'ma-24')
  const logging = getMaturityScore(maturityResponses, 'ma-26')
  const segmentation = getMaturityScore(maturityResponses, 'ma-56')
  const soc247 = getMaturityScore(maturityResponses, 'ma-59')

  // If no relevant maturity data answered, skip adjustments
  const scores = [mfa, edr, backupTesting, patching, logging, segmentation, soc247]
  if (scores.every(s => s === null)) return null

  // --- Recovery time multiplier (affects ransomware/BI downtime) ---
  // Based on backup testing maturity:
  //   no (0)       → 2.0x downtime (untested backups, full rebuild likely)
  //   partially (1.5) → 1.3x (some testing, uncertain recovery)
  //   mostly (2.5) → 0.8x (regular testing, faster recovery)
  //   yes (3.0)    → 0.5x (validated restores, rapid recovery)
  let recoveryTimeMultiplier = 1.0
  if (backupTesting !== null) {
    if (backupTesting === 0) recoveryTimeMultiplier = 2.0
    else if (backupTesting <= 1.5) recoveryTimeMultiplier = 1.3
    else if (backupTesting <= 2.5) recoveryTimeMultiplier = 0.8
    else recoveryTimeMultiplier = 0.5
  }

  // --- Detection speed multiplier (affects breach cost through exposure duration) ---
  // IBM/Ponemon: orgs with SOC detect in ~70 days vs 230+ without.
  // Combines: 24/7 monitoring (ma-59), centralized logging (ma-26), EDR (ma-22)
  let detectionMultiplier = 1.0
  const detectionScores = [soc247, logging, edr].filter(s => s !== null)
  if (detectionScores.length > 0) {
    const avgDetection = detectionScores.reduce((a, b) => a + b, 0) / detectionScores.length
    // Scale: avg 0 → 1.4x (slow detection, more exposure)
    //        avg 1.5 → 1.0x (baseline)
    //        avg 3.0 → 0.65x (fast detection, contained quickly)
    detectionMultiplier = 1.4 - (avgDetection / 3.0) * 0.75
  }

  // --- Credential attack probability multiplier (BEC, credential theft) ---
  // MFA is the single most impactful control for credential-based attacks
  let credentialAttackMultiplier = 1.0
  if (mfa !== null) {
    if (mfa === 0) credentialAttackMultiplier = 1.5       // No MFA → much higher BEC/credential risk
    else if (mfa <= 1.5) credentialAttackMultiplier = 1.15 // Partial MFA
    else if (mfa <= 2.5) credentialAttackMultiplier = 0.75 // Most accounts covered
    else credentialAttackMultiplier = 0.55                  // Full MFA including service accounts
  }

  // --- Propagation scope multiplier (affects ransomware blast radius) ---
  // Network segmentation limits lateral movement
  let propagationMultiplier = 1.0
  if (segmentation !== null) {
    if (segmentation === 0) propagationMultiplier = 1.4     // Flat network → full spread
    else if (segmentation <= 1.5) propagationMultiplier = 1.1
    else if (segmentation <= 2.5) propagationMultiplier = 0.8
    else propagationMultiplier = 0.6                         // Microsegmentation → contained
  }

  // --- Vulnerability exposure multiplier (unpatched systems) ---
  let patchingMultiplier = 1.0
  if (patching !== null) {
    if (patching === 0) patchingMultiplier = 1.4
    else if (patching <= 1.5) patchingMultiplier = 1.15
    else if (patching <= 2.5) patchingMultiplier = 0.85
    else patchingMultiplier = 0.7
  }

  return {
    recoveryTimeMultiplier,
    detectionMultiplier,
    credentialAttackMultiplier,
    propagationMultiplier,
    patchingMultiplier,
    controlScores: {
      mfa, edr, backupTesting, patching, logging, segmentation, soc247
    }
  }
}

/**
 * Calculate context-aware multiplier for a specific scenario
 * Combines industry, business context, org characteristics, and framework adjustments
 */
export function calculateContextMultiplier(
  scenarioId,
  verticalKey = 'TP-DEFAULT',
  businessScopingResponses = {},
  orgCharacteristics = [],
  frameworks = []
) {
  let multiplier = 1.0

  // Apply industry multiplier
  const industryMults = INDUSTRY_MULTIPLIERS[verticalKey] || INDUSTRY_MULTIPLIERS['TP-DEFAULT']
  multiplier *= industryMults[scenarioId] || 1.0

  // Apply business scoping context adjustments
  Object.entries(CONTEXT_FACTOR_ADJUSTMENTS).forEach(([questionId, adjustments]) => {
    const response = businessScopingResponses[questionId]
    if (response) {
      const responseKey = typeof response === 'object' ? response.value : response
      const adjustment = adjustments[responseKey]
      if (adjustment && adjustment[scenarioId]) {
        multiplier *= adjustment[scenarioId]
      }
    }
  })

  // Apply organizational characteristic adjustments
  orgCharacteristics.forEach(characteristic => {
    const adjustment = ORG_CHARACTERISTIC_ADJUSTMENTS[characteristic]
    if (adjustment && adjustment[scenarioId]) {
      multiplier *= adjustment[scenarioId]
    }
  })

  // Apply framework adjustments
  frameworks.forEach(framework => {
    const adjustment = FRAMEWORK_ADJUSTMENTS[framework]
    if (adjustment && adjustment[scenarioId]) {
      multiplier *= adjustment[scenarioId]
    }
  })

  // Cap multiplier to reasonable bounds (0.5x to 3.0x)
  return Math.min(3.0, Math.max(0.5, multiplier))
}

/**
 * Calculate gross loss for a specific scenario with full context awareness
 */
export function calculateScenarioLoss(
  scenarioId,
  impactData,
  verticalKey = 'TP-DEFAULT',
  businessScopingResponses = null,
  orgCharacteristics = [],
  frameworks = [],
  sensitiveDataVolumes = null,
  maturityResponses = null
) {
  const scenario = LOSS_SCENARIOS[scenarioId]
  if (!scenario) return null

  // Get context-aware multiplier
  const contextMultiplier = calculateContextMultiplier(
    scenarioId,
    verticalKey,
    businessScopingResponses || {},
    orgCharacteristics,
    frameworks
  )

  // Get segment-adjusted parameters
  const segmentParams = businessScopingResponses
    ? getSegmentAdjustedParams(businessScopingResponses, 'likely')
    : null

  // Enrich base components with claims benchmark data when available
  const claimsEnrichment = getClaimsEnrichedBaseComponents(
    scenarioId,
    verticalKey,
    impactData?.estimated_annual_revenue
  )
  const components = { ...claimsEnrichment.enrichedComponents }
  let totalLoss = 0

  // Get maturity-informed control adjustments
  const maturityAdj = getMaturityControlAdjustments(maturityResponses)

  // Scenario-specific calculations
  switch (scenarioId) {
    case 'data_breach':
      // Per-category breach costs (when sensitive data volumes are available)
      if (sensitiveDataVolumes && sensitiveDataVolumes.length > 0) {
        const categoryResult = calculateCategoryBreachCosts(sensitiveDataVolumes)
        if (categoryResult.hasData) {
          components.recordCosts = categoryResult.totalCost
          components.categoryBreakdown = categoryResult.breakdown
          // Credit monitoring based on total records across all categories
          const totalRecords = sensitiveDataVolumes.reduce((sum, v) => sum + (v.estimated_record_count || 0), 0)
          const effectiveRecordsForMonitoring = Math.min(totalRecords, 100000) +
            (totalRecords > 100000 ? Math.log10(totalRecords - 100000) * 15000 : 0)
          components.creditMonitoring = Math.round(effectiveRecordsForMonitoring * 25)
          delete components.perRecordCost
        }
      }
      // Fallback: flat-rate calculation using aggregate data_record_count
      if (!components.recordCosts && impactData?.data_record_count) {
        const costPerRecord = segmentParams?.costPerRecord || components.perRecordCost
        // Logarithmic scaling for large record counts
        const effectiveRecords = Math.min(impactData.data_record_count, 100000) +
          (impactData.data_record_count > 100000
            ? Math.log10(impactData.data_record_count - 100000) * 15000
            : 0)
        components.recordCosts = Math.round(effectiveRecords * costPerRecord)
        components.creditMonitoring = Math.round(effectiveRecords * 25) // $25/person for monitoring
        delete components.perRecordCost
      }
      // Detection speed affects breach scope — faster detection = less data exposed
      if (maturityAdj) {
        if (components.forensicsBase) components.forensicsBase = Math.round(components.forensicsBase * maturityAdj.detectionMultiplier)
        if (components.customerChurn) components.customerChurn = Math.round(components.customerChurn * maturityAdj.detectionMultiplier)
        if (components.prCrisis) components.prCrisis = Math.round(components.prCrisis * maturityAdj.detectionMultiplier)
        // MFA reduces credential-based breach probability
        if (components.recordCosts) components.recordCosts = Math.round(components.recordCosts * maturityAdj.credentialAttackMultiplier)
      }
      // Adjust regulatory fine based on context
      if (frameworks.includes('hipaa')) {
        components.regulatoryFine = 250000
      } else if (frameworks.includes('gdpr')) {
        components.regulatoryFine = 500000
      } else if (frameworks.includes('ccpa')) {
        components.regulatoryFine = 150000
      }
      break

    case 'cyber_extortion':
    case 'business_interruption': {
      // Calculate downtime costs — adjusted by backup testing maturity
      let downtimeHours = segmentParams?.downtimeDays
        ? segmentParams.downtimeDays * 24
        : scenario.averageDowntimeHours

      // Backup testing maturity adjusts recovery time
      if (maturityAdj) {
        downtimeHours = Math.round(downtimeHours * maturityAdj.recoveryTimeMultiplier)
      }

      // Use per-system BIA data if available, otherwise fall back to flat rate
      const biaSystems = impactData?.criticalSystems?.filter(s => s.hourly_cost_impact != null)
      if (downtimeHours && biaSystems?.length > 0) {
        // For extortion: use critical + high tier systems (most likely to be impacted)
        // For business interruption: use all systems weighted by revenue dependency
        const relevantSystems = scenarioId === 'cyber_extortion'
          ? biaSystems.filter(s => s.criticality_tier === 'critical' || s.criticality_tier === 'high')
          : biaSystems
        const systemsToUse = relevantSystems.length > 0 ? relevantSystems : biaSystems
        const totalRevDep = systemsToUse.reduce((sum, s) => sum + (s.revenue_dependency_pct || 0), 0)
        let effectiveHourlyCost
        if (totalRevDep > 0) {
          effectiveHourlyCost = systemsToUse.reduce((sum, s) => {
            const weight = (s.revenue_dependency_pct || 0) / totalRevDep
            return sum + (s.hourly_cost_impact || 0) * weight
          }, 0)
        } else {
          effectiveHourlyCost = systemsToUse.reduce((sum, s) => sum + (s.hourly_cost_impact || 0), 0) / systemsToUse.length
        }
        components.downtimeCost = Math.round(downtimeHours * effectiveHourlyCost)
      } else if (downtimeHours && impactData?.downtime_cost_per_hour) {
        components.downtimeCost = Math.round(downtimeHours * impactData.downtime_cost_per_hour)
      } else if (downtimeHours && impactData?.estimated_annual_revenue) {
        const estimatedHourlyCost = impactData.estimated_annual_revenue / (365 * 8)
        components.downtimeCost = Math.round(downtimeHours * estimatedHourlyCost * 0.15)
      }

      // Network segmentation limits ransomware propagation scope
      if (scenarioId === 'cyber_extortion' && maturityAdj) {
        components.recoveryLabor = Math.round((components.recoveryLabor || 0) * maturityAdj.propagationMultiplier)
        components.dataRecoveryTools = Math.round((components.dataRecoveryTools || 0) * maturityAdj.propagationMultiplier)
      }
      break
    }

    case 'financial_theft':
      // Scale based on revenue/transaction volume
      if (impactData?.estimated_annual_revenue) {
        // Average theft is ~0.5-2% of transaction volume
        const transactionRisk = impactData.estimated_annual_revenue * 0.008
        components.averageLoss = Math.max(components.averageLoss, Math.min(transactionRisk, 500000))
      }
      // MFA maturity directly reduces BEC/credential theft success rate
      if (maturityAdj) {
        components.averageLoss = Math.round(components.averageLoss * maturityAdj.credentialAttackMultiplier)
      }
      break

    case 'regulatory':
      // Adjust fine based on revenue (GDPR-style percentage)
      if (impactData?.estimated_annual_revenue && frameworks.includes('gdpr')) {
        components.regulatoryFine = Math.min(
          impactData.estimated_annual_revenue * 0.02, // 2% of revenue
          500000 // Cap for mid-market
        )
      }
      break

    case 'insider_threat':
      // Scale based on employee count and data sensitivity
      if (impactData?.employee_count > 500) {
        components.dataTheftValue *= 1.5
        components.investigationCost *= 1.3
      }
      break

    case 'third_party':
      // Adjust based on vendor ecosystem complexity
      if (segmentParams?.segment === 'enterprise' || segmentParams?.segment === 'upper-mid') {
        components.vendorBreachShare *= 1.4
        components.settlementReserve *= 1.3
      }
      break
  }

  // Apply segment-adjusted recovery costs
  if (segmentParams?.recoveryCost && components.investigationCost) {
    components.investigationCost = Math.round(
      components.investigationCost * (segmentParams.recoveryCost / 50000)
    )
  }

  // Apply patching/vulnerability exposure as a general multiplier across all scenarios
  // Poor patching increases overall attack surface and incident severity
  if (maturityAdj && maturityAdj.patchingMultiplier !== 1.0) {
    Object.keys(components).forEach(key => {
      if (typeof components[key] === 'number' && key !== 'downtimeCost') {
        components[key] = Math.round(components[key] * maturityAdj.patchingMultiplier)
      }
    })
  }

  // Sum all components
  Object.values(components).forEach(cost => {
    if (typeof cost === 'number') {
      totalLoss += cost
    }
  })

  // Apply context multiplier
  const adjustedLoss = Math.round(totalLoss * contextMultiplier)

  return {
    scenarioId,
    scenarioName: scenario.name,
    shortName: scenario.shortName,
    description: scenario.description,
    detailedDescription: scenario.detailedDescription,
    icon: scenario.icon,
    category: scenario.category,
    coverageType: scenario.coverageType,
    contextFactors: scenario.contextFactors,
    components,
    grossLoss: adjustedLoss,
    contextMultiplier: Math.round(contextMultiplier * 100) / 100,
    baseLoss: totalLoss,
    segmentUsed: segmentParams?.segment || null,
    segmentLabel: segmentParams?.segmentLabel || null,
    claimsEnrichment: claimsEnrichment.enrichmentApplied ? {
      source: claimsEnrichment.claimsSource,
      sector: claimsEnrichment.sectorUsed,
      revenueBand: claimsEnrichment.revenueBandUsed
    } : null,
    maturityAdjustments: maturityAdj ? {
      recoveryTime: maturityAdj.recoveryTimeMultiplier,
      detection: maturityAdj.detectionMultiplier,
      credentialAttack: maturityAdj.credentialAttackMultiplier,
      propagation: maturityAdj.propagationMultiplier,
      patching: maturityAdj.patchingMultiplier,
      controlScores: maturityAdj.controlScores
    } : null
  }
}

/**
 * Calculate probability of occurrence based on maturity level
 */
export function calculateScenarioProbability(scenarioId, maturityScore) {
  const scenario = LOSS_SCENARIOS[scenarioId]
  if (!scenario) return 0.15

  const probabilities = scenario.probabilityByMaturity

  let probability
  if (maturityScore >= 90) {
    probability = probabilities.optimizing
  } else if (maturityScore >= 70) {
    probability = probabilities.managed
  } else if (maturityScore >= 50) {
    probability = probabilities.defined
  } else if (maturityScore >= 25) {
    probability = probabilities.developing
  } else {
    probability = probabilities.initial
  }

  return Math.round(probability * 100) / 100
}

/**
 * Calculate Annualized Loss Expectancy (ALE) with full context
 * ALE = Single Loss Expectancy (SLE) × Annual Rate of Occurrence (ARO)
 */
export function calculateALE(
  scenarioId,
  impactData,
  maturityScore,
  verticalKey = 'TP-DEFAULT',
  businessScopingResponses = null,
  orgCharacteristics = [],
  frameworks = [],
  sensitiveDataVolumes = null,
  maturityResponses = null
) {
  const lossCalc = calculateScenarioLoss(
    scenarioId,
    impactData,
    verticalKey,
    businessScopingResponses,
    orgCharacteristics,
    frameworks,
    sensitiveDataVolumes,
    maturityResponses
  )
  const probability = calculateScenarioProbability(scenarioId, maturityScore)

  if (!lossCalc) return null

  const ale = Math.round(lossCalc.grossLoss * probability)

  return {
    ...lossCalc,
    probability,
    ale,
    probabilityPercent: Math.round(probability * 100),
    aleFormatted: formatCurrency(ale)
  }
}

/**
 * Apply insurance offset to calculate net exposure
 */
export function applyInsuranceOffset(grossLoss, impactData) {
  if (!impactData?.has_cyber_insurance) {
    return {
      grossLoss,
      insuranceCoverage: 0,
      clientRetention: 0,
      netExposure: grossLoss,
      coveragePercent: 0
    }
  }

  const coverageLimit = impactData.insurance_coverage_limit || 0
  const clientRetention = impactData.client_retention ?? impactData.insurance_deductible ?? 0

  const lossAfterRetention = Math.max(0, grossLoss - clientRetention)
  const maxPayout = Math.max(0, coverageLimit - clientRetention)
  const insurancePayout = Math.min(lossAfterRetention, maxPayout)

  const netExposure = Math.max(0, grossLoss - insurancePayout)
  const coveragePercent = grossLoss > 0 ? Math.round((insurancePayout / grossLoss) * 100) : 0

  return {
    grossLoss,
    clientRetention,
    coverageLimit,
    insurancePayout,
    netExposure,
    coveragePercent,
    hasAdequateCoverage: insurancePayout >= lossAfterRetention * 0.7
  }
}

/**
 * Calculate all loss scenarios with full context awareness
 */
export function calculateLossScenarios(
  impactData,
  threatContext,
  profile,
  maturityScore,
  businessScopingResponses = null,
  orgCharacteristics = [],
  frameworks = [],
  sensitiveDataVolumes = null,
  maturityResponses = null
) {
  const verticalKey = profile?.verticalKey || 'TP-DEFAULT'
  const scenarios = []

  const segmentParams = businessScopingResponses
    ? getSegmentAdjustedParams(businessScopingResponses, 'likely')
    : null

  Object.keys(LOSS_SCENARIOS).forEach(scenarioId => {
    const aleCalc = calculateALE(
      scenarioId,
      impactData,
      maturityScore,
      verticalKey,
      businessScopingResponses,
      orgCharacteristics,
      frameworks,
      sensitiveDataVolumes,
      maturityResponses
    )
    if (!aleCalc) return

    const insuranceCalc = applyInsuranceOffset(aleCalc.grossLoss, impactData)

    scenarios.push({
      ...aleCalc,
      insurance: insuranceCalc,
      netExposure: insuranceCalc.netExposure,
      annualizedNetExposure: Math.round(insuranceCalc.netExposure * aleCalc.probability),
      severity: getLossSeverityLevel(aleCalc.ale, impactData?.estimated_annual_revenue)
    })
  })

  // Sort by annualized net exposure (highest first)
  scenarios.sort((a, b) => b.annualizedNetExposure - a.annualizedNetExposure)

  // Calculate aggregate metrics
  const totalGrossALE = scenarios.reduce((sum, s) => sum + s.ale, 0)
  const totalNetALE = scenarios.reduce((sum, s) => sum + s.annualizedNetExposure, 0)
  const maxSingleLoss = Math.max(...scenarios.map(s => s.grossLoss))

  // Identify top risks
  const criticalScenarios = scenarios.filter(s => s.severity.level === 'Critical')
  const highScenarios = scenarios.filter(s => s.severity.level === 'High')

  return {
    scenarios,
    summary: {
      totalGrossALE,
      totalNetALE,
      maxSingleLoss,
      hasInsurance: impactData?.has_cyber_insurance || false,
      insuranceGap: impactData?.has_cyber_insurance
        ? Math.max(0, maxSingleLoss - (impactData.insurance_coverage_limit || 0))
        : maxSingleLoss,
      highestRiskScenario: scenarios[0]?.scenarioName || 'Unknown',
      criticalCount: criticalScenarios.length,
      highCount: highScenarios.length,
      averageProbability: scenarios.length > 0
        ? Math.round((scenarios.reduce((sum, s) => sum + s.probability, 0) / scenarios.length) * 100)
        : 0,
      marketSegment: segmentParams?.segment || null,
      marketSegmentLabel: segmentParams?.segmentLabel || null,
      contextFactorsApplied: {
        vertical: verticalKey,
        orgCharacteristics: orgCharacteristics.length,
        frameworks: frameworks.length
      }
    }
  }
}

/**
 * Determine loss severity level — hybrid model that takes the worse of:
 *   (a) absolute dollar magnitude (an advisor's gut check — a $5M loss is
 *       material in any room, regardless of company size)
 *   (b) percentage of annual revenue (a percentage-based view that scales
 *       with the org).
 *
 * For very large enterprises (e.g. $1B+ revenue), pure-percentage tiers can
 * mark even multi-million-dollar losses as "Low" because they're a tiny
 * fraction of revenue. The hybrid prevents that distortion.
 */
const SEVERITY_RANK = { Low: 1, Medium: 2, High: 3, Critical: 4 }

function classifyByAbsolute(ale) {
  if (ale >= 5000000) return { level: 'Critical', color: 'red', description: 'Severe absolute financial impact' }
  if (ale >= 1000000) return { level: 'High', color: 'orange', description: 'Significant absolute financial impact' }
  if (ale >= 250000) return { level: 'Medium', color: 'yellow', description: 'Moderate absolute financial impact' }
  return { level: 'Low', color: 'green', description: 'Limited absolute financial impact' }
}

function classifyByPercent(percentOfRevenue) {
  if (percentOfRevenue >= 5) return { level: 'Critical', color: 'red', description: 'Could threaten business viability' }
  if (percentOfRevenue >= 2) return { level: 'High', color: 'orange', description: 'Significant budget impact' }
  if (percentOfRevenue >= 0.5) return { level: 'Medium', color: 'yellow', description: 'Notable but manageable' }
  return { level: 'Low', color: 'green', description: 'Within normal risk tolerance' }
}

export function getLossSeverityLevel(ale, annualRevenue) {
  const absolute = classifyByAbsolute(ale)
  if (!annualRevenue || annualRevenue <= 0) return absolute

  const percentOfRevenue = (ale / annualRevenue) * 100
  const relative = classifyByPercent(percentOfRevenue)

  // Worse of the two wins. Equal-rank ties prefer the relative classification's
  // description (which mentions revenue context) but keep the absolute level.
  const winner = SEVERITY_RANK[absolute.level] >= SEVERITY_RANK[relative.level] ? absolute : relative
  return {
    ...winner,
    percentOfRevenue: Math.round(percentOfRevenue * 10) / 10
  }
}

/**
 * Estimate downtime cost per hour based on organization profile
 */
export function estimateDowntimeCost(impactData) {
  const revenue = impactData?.estimated_annual_revenue || 5000000
  const employees = impactData?.employee_count || 50

  const hourlyRevenue = revenue / (250 * 8)
  const hourlyLabor = employees * 75 * 0.3

  const estimatedCost = Math.round((hourlyRevenue * 0.6) + (hourlyLabor * 0.4))

  return {
    estimated: estimatedCost,
    revenueComponent: Math.round(hourlyRevenue),
    laborComponent: Math.round(hourlyLabor),
    methodology: 'Based on revenue impact and employee productivity loss'
  }
}

/**
 * Format currency for display
 */
export function formatCurrency(amount) {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`
  }
  return `$${amount.toFixed(0)}`
}

/**
 * Get all available loss scenario types with metadata
 */
export function getLossScenarioTypes() {
  return Object.entries(LOSS_SCENARIOS).map(([id, scenario]) => ({
    id,
    name: scenario.name,
    shortName: scenario.shortName,
    description: scenario.description,
    detailedDescription: scenario.detailedDescription,
    icon: scenario.icon,
    category: scenario.category,
    coverageType: scenario.coverageType,
    contextFactors: scenario.contextFactors
  }))
}

/**
 * Calculate insurance adequacy assessment
 */
export function assessInsuranceAdequacy(impactData, lossScenarios) {
  if (!impactData?.has_cyber_insurance) {
    return {
      hasInsurance: false,
      adequacy: 'None',
      recommendation: 'Consider obtaining cyber insurance coverage',
      gaps: ['No cyber insurance policy in place']
    }
  }

  const coverageLimit = impactData.insurance_coverage_limit || 0
  const maxLoss = lossScenarios?.summary?.maxSingleLoss || 0
  const totalALE = lossScenarios?.summary?.totalGrossALE || 0

  const gaps = []

  // Claims-backed SIR benchmarking
  const claimsSIR = claimsBenchmarks.insuranceBenchmarks?.sme
  if (claimsSIR) {
    const clientRetentionVal = impactData.client_retention ?? impactData.insurance_deductible ?? 0
    if (clientRetentionVal > 0 && claimsSIR.averageSIR > 0) {
      if (clientRetentionVal > claimsSIR.averageSIR * 2) {
        gaps.push(`Client retention (${formatCurrency(clientRetentionVal)}) is more than double the industry average SIR (${formatCurrency(claimsSIR.averageSIR)})`)
      }
    }
  }

  if (coverageLimit < maxLoss) {
    gaps.push(`Coverage limit (${formatCurrency(coverageLimit)}) below max single loss (${formatCurrency(maxLoss)})`)
  }

  const clientRetentionValue = impactData.client_retention ?? impactData.insurance_deductible ?? 0
  if (clientRetentionValue > impactData.estimated_annual_revenue * 0.02) {
    gaps.push('Client retention exceeds 2% of annual revenue')
  }

  if (coverageLimit < totalALE * 2) {
    gaps.push('Coverage may be insufficient for multiple incidents in a year')
  }

  // Check for scenario-specific coverage gaps
  const criticalScenarios = lossScenarios?.scenarios?.filter(s => s.severity.level === 'Critical') || []
  if (criticalScenarios.length > 0) {
    const criticalNames = criticalScenarios.map(s => s.shortName).join(', ')
    gaps.push(`Critical exposure in: ${criticalNames}`)
  }

  let adequacy = 'Adequate'
  let recommendation = 'Current coverage appears appropriate'

  if (gaps.length >= 3) {
    adequacy = 'Inadequate'
    recommendation = 'Review and increase coverage limits'
  } else if (gaps.length >= 1) {
    adequacy = 'Partial'
    recommendation = 'Consider increasing coverage or reducing client retention'
  }

  return {
    hasInsurance: true,
    adequacy,
    recommendation,
    gaps,
    coverageRatio: maxLoss > 0 ? Math.round((coverageLimit / maxLoss) * 100) : 100,
    claimsBenchmarkContext: claimsBenchmarks.insuranceBenchmarks?.sme ? {
      avgSIR: claimsBenchmarks.insuranceBenchmarks.sme.averageSIR,
      avgPayoutRatio: claimsBenchmarks.insuranceBenchmarks.sme.payoutToIncidentCostRatio,
      uninsuredExposureRatio: claimsBenchmarks.insuranceBenchmarks.sme.uninsuredExposureRatio,
      source: 'netdiligence-2025',
      dataWindow: '2020-2024'
    } : null
  }
}

/**
 * Ruin Theory Analysis
 *
 * Calculates the probability of financial ruin from cumulative cyber incidents
 * using actuarial concepts adapted for cybersecurity risk.
 *
 * Key concepts:
 * - Probability of Ruin: Likelihood that cumulative losses exceed available capital
 * - Time to Ruin: Expected time until financial stress given loss patterns
 * - Capital Adequacy: Required reserves to achieve target survival probability
 * - Safety Loading: Margin above expected losses needed to avoid ruin
 */

/**
 * Calculate ruin probability using Cramér-Lundberg approximation
 * ψ(u) ≈ ρ * e^(-Ru) where:
 *   - u = initial surplus (reserves + available coverage)
 *   - R = adjustment coefficient (Lundberg exponent)
 *   - ρ = loading factor adjustment
 *
 * @param {Object} params - Ruin calculation parameters
 * @returns {Object} Ruin analysis results
 */
export function calculateRuinProbability(params) {
  const {
    totalGrossALE,           // Expected annual losses (from all scenarios)
    maxSingleLoss,           // Largest potential single incident
    cyberBudget,             // Available annual cyber budget/reserves
    insuranceCoverage,       // Insurance coverage limit
    clientRetention,         // Deductible/SIR amount
    timeHorizonYears = 3,    // Planning horizon (default 3 years)
    annualRevenue,           // For context on materiality
    hasInsurance = false
  } = params

  // Validate inputs - need minimum data for meaningful analysis
  if (!totalGrossALE || totalGrossALE <= 0) {
    return {
      canCalculate: false,
      reason: 'Insufficient loss data to calculate ruin probability'
    }
  }

  // Calculate effective surplus (what's available to absorb losses)
  // Surplus = available reserves + insurance coverage (minus retention)
  const effectiveInsurance = hasInsurance
    ? Math.max(0, (insuranceCoverage || 0) - (clientRetention || 0))
    : 0
  const surplus = (cyberBudget || 0) + effectiveInsurance

  // If no surplus/budget defined, use a reasonable default based on revenue
  const effectiveSurplus = surplus > 0
    ? surplus
    : (annualRevenue ? annualRevenue * 0.02 : totalGrossALE * 1.5) // 2% of revenue or 1.5x ALE

  // Calculate safety loading (how much buffer exists above expected losses)
  // Positive loading = budget exceeds expected losses
  // Negative loading = underfunded
  const safetyLoading = effectiveSurplus - totalGrossALE
  const loadingRatio = totalGrossALE > 0 ? safetyLoading / totalGrossALE : 0

  // Calculate claim size distribution parameters
  // Using exponential approximation for cyber losses
  const averageClaimSize = totalGrossALE / 7 // Assume ~7 potential incident types
  const claimVariance = Math.pow(maxSingleLoss - averageClaimSize, 2) / 4

  // Calculate adjustment coefficient (R) - Lundberg exponent
  // Higher R = faster decay of ruin probability with increasing surplus
  // R ≈ 2μ / σ² where μ = loading, σ² = claim variance
  // Simplified: R = 2 * safetyLoading / maxSingleLoss² (accounts for tail risk)
  let adjustmentCoefficient
  if (safetyLoading > 0 && maxSingleLoss > 0) {
    // Use modified formula that accounts for both loading and maximum exposure
    adjustmentCoefficient = (2 * Math.abs(safetyLoading)) / (maxSingleLoss * maxSingleLoss)
    // Cap to reasonable bounds
    adjustmentCoefficient = Math.min(0.00001, Math.max(0.0000001, adjustmentCoefficient))
  } else {
    // Negative loading = very high ruin risk
    adjustmentCoefficient = 0.0000001 // Very small = high ruin probability
  }

  // Calculate ruin probability using Cramér-Lundberg approximation
  // ψ(u) ≈ e^(-Ru) for large u
  let ruinProbability
  if (safetyLoading <= 0) {
    // Underfunded: certain ruin over infinite horizon
    // For finite horizon, use time-adjusted probability
    ruinProbability = Math.min(0.99, 0.5 + (Math.abs(loadingRatio) * 0.3) * timeHorizonYears)
  } else {
    // Apply Cramér-Lundberg formula
    ruinProbability = Math.exp(-adjustmentCoefficient * effectiveSurplus)
    // Adjust for finite time horizon (ruin is less likely in shorter periods)
    ruinProbability = 1 - Math.pow(1 - ruinProbability, timeHorizonYears / 10)
    // Apply floor and ceiling
    ruinProbability = Math.min(0.99, Math.max(0.001, ruinProbability))
  }

  // Calculate required reserves for various survival probabilities
  const calculateRequiredReserves = (targetSurvival) => {
    if (adjustmentCoefficient <= 0) return Infinity
    const targetRuin = 1 - targetSurvival
    return Math.round(-Math.log(targetRuin) / adjustmentCoefficient)
  }

  // Time to expected ruin (if underfunded)
  // Based on random walk with drift
  let expectedTimeToRuin = null
  if (loadingRatio < 0) {
    // Negative drift: calculate expected time to deplete reserves
    const annualDeficit = Math.abs(safetyLoading)
    expectedTimeToRuin = effectiveSurplus / annualDeficit
  }

  // Capital adequacy assessment
  let capitalAdequacy = {
    level: 'Adequate',
    color: 'green',
    recommendation: 'Current reserves appear sufficient for expected cyber losses'
  }

  if (ruinProbability > 0.25) {
    capitalAdequacy = {
      level: 'Critical',
      color: 'red',
      recommendation: 'Immediate action needed: reserves are significantly underfunded relative to cyber risk exposure'
    }
  } else if (ruinProbability > 0.10) {
    capitalAdequacy = {
      level: 'Concerning',
      color: 'orange',
      recommendation: 'Consider increasing cyber reserves or insurance coverage to reduce financial vulnerability'
    }
  } else if (ruinProbability > 0.05) {
    capitalAdequacy = {
      level: 'Marginal',
      color: 'yellow',
      recommendation: 'Reserves are adequate for typical scenarios but may be stressed by severe incidents'
    }
  }

  // Calculate materiality thresholds relative to revenue
  let materialityAnalysis = null
  if (annualRevenue && annualRevenue > 0) {
    const aleAsPercentRevenue = (totalGrossALE / annualRevenue) * 100
    const maxLossAsPercentRevenue = (maxSingleLoss / annualRevenue) * 100

    materialityAnalysis = {
      aleAsPercentRevenue: Math.round(aleAsPercentRevenue * 100) / 100,
      maxLossAsPercentRevenue: Math.round(maxLossAsPercentRevenue * 100) / 100,
      isMaterial: aleAsPercentRevenue > 1, // >1% of revenue is material
      isExistential: maxLossAsPercentRevenue > 15 // >15% of revenue is existential
    }
  }

  // Monte Carlo-inspired scenario analysis
  // Calculate probability of various loss levels over the time horizon
  const scenarioAnalysis = {
    noSignificantLoss: Math.pow(1 - 0.15, timeHorizonYears), // ~15% annual incident rate
    moderateLoss: 1 - Math.pow(1 - 0.08, timeHorizonYears),  // 8% chance of moderate loss
    severeLoss: 1 - Math.pow(1 - 0.03, timeHorizonYears),    // 3% chance of severe loss
    catastrophic: 1 - Math.pow(1 - 0.01, timeHorizonYears)   // 1% chance of catastrophic
  }

  return {
    canCalculate: true,

    // Primary metrics
    ruinProbability: Math.round(ruinProbability * 10000) / 100, // As percentage with 2 decimals
    survivalProbability: Math.round((1 - ruinProbability) * 10000) / 100,
    timeHorizonYears,

    // Capital analysis
    effectiveSurplus,
    safetyLoading,
    loadingRatio: Math.round(loadingRatio * 100) / 100,
    isUnderfunded: safetyLoading < 0,

    // Required reserves for target survival rates
    requiredReserves: {
      for90Percent: calculateRequiredReserves(0.90),
      for95Percent: calculateRequiredReserves(0.95),
      for99Percent: calculateRequiredReserves(0.99)
    },

    // Time analysis
    expectedTimeToRuin,

    // Assessments
    capitalAdequacy,
    materialityAnalysis,
    scenarioAnalysis,

    // Technical parameters (for advanced users)
    technicalParams: {
      adjustmentCoefficient,
      averageClaimSize: Math.round(averageClaimSize),
      claimVariance: Math.round(claimVariance)
    }
  }
}

/**
 * Get risk tolerance interpretation based on ruin probability
 */
export function getRuinRiskLevel(ruinProbability) {
  if (ruinProbability >= 25) {
    return {
      level: 'Unacceptable',
      color: 'red',
      icon: 'exclamation-triangle',
      description: 'High probability of financial distress from cyber incidents'
    }
  }
  if (ruinProbability >= 10) {
    return {
      level: 'Elevated',
      color: 'orange',
      icon: 'exclamation',
      description: 'Meaningful risk of financial strain from cyber losses'
    }
  }
  if (ruinProbability >= 5) {
    return {
      level: 'Moderate',
      color: 'yellow',
      icon: 'information-circle',
      description: 'Some vulnerability to severe cyber incidents'
    }
  }
  if (ruinProbability >= 1) {
    return {
      level: 'Acceptable',
      color: 'green',
      icon: 'check-circle',
      description: 'Well-prepared to absorb expected cyber losses'
    }
  }
  return {
    level: 'Strong',
    color: 'emerald',
    icon: 'shield-check',
    description: 'Excellent financial resilience to cyber risk'
  }
}

/**
 * Export scenario metadata for external use
 */
export { LOSS_SCENARIOS, INDUSTRY_MULTIPLIERS, getClaimsBenchmarkForSector, getClaimsBenchmarkForRevenueBand }
